import { getLayer } from '../layerService.js';
import { exportPageLogic, restorePageLogic } from '../../logic/pageLogic.js';
import { exportPageHass, restorePageHass } from '../../hass/pageHass.js';

/**
 * Snapshot a page and all its children into a plain lines array.
 */
export function snapshotPage(page) {
    const rawLines = [];
    page.export(page, rawLines, false);
    // Objects are recreated with new uuids on undo, so keep the graphs in
    // their file form (OpenHASP id references) on the page header.
    const logic = exportPageLogic(page);
    const hass = exportPageHass(page);
    const header = rawLines.find(line => line.page !== undefined && line.id === undefined);
    if (logic && header) header.logic = logic;
    if (hass && header) header.hass = hass;
    return JSON.parse(JSON.stringify(rawLines));
}

export default class PageDeleteCommand {
    constructor({ pageUuid, pageHaspId, pageObj, pageLines }) {
        this.id = crypto.randomUUID();
        this.timestamp = Date.now();
        this.description = 'Delete page P' + pageHaspId;
        this.category = 'structure';
        this.pageUuid = pageUuid;
        this.pageHaspId = pageHaspId;
        this.pageObj = pageObj ?? 'page';
        this.pageLines = pageLines; // full export: [{page:N}, {id:1,obj:...}, ...]
    }

    execute() {
        const layer = getLayer();
        if (!layer) return;
        const page = layer.findOne('#' + this.pageUuid) ?? layer.findPage(this.pageHaspId);
        if (!page) return;
        const other = layer.children.find(c => c !== page && (c.obj === 'page' || c.obj === 'template_page') && c.haspid !== 0);
        if (other) layer.switchToPage(other);
        page.destroy();
        layer.batchDraw();
    }

    undo() {
        const layer = getLayer();
        if (!layer) return;

        // Recreate the page with the original haspid and UUID
        layer.addPage({ obj: this.pageObj, page: this.pageHaspId, _uuid: this.pageUuid });
        const page = layer.findPage(this.pageHaspId);
        if (page) layer.switchToPage(page);

        // Reimport all objects that belonged to this page
        for (const line of this.pageLines) {
            if (line.page !== undefined && line.id === undefined) continue; // page header
            if (line.id !== undefined) layer.importObject({ ...line });
        }
        const header = this.pageLines.find(line => line.page !== undefined && line.id === undefined);
        if (page && header?.logic) {
            page._pendingLogic = structuredClone(header.logic);
            restorePageLogic(page);
        }
        if (page && header?.hass) {
            page._pendingHass = structuredClone(header.hass);
            restorePageHass(page);
        }

        layer.batchDraw();
    }

    toJSON() {
        return {
            type: 'PageDelete',
            id: this.id,
            timestamp: this.timestamp,
            payload: {
                pageUuid: this.pageUuid,
                pageHaspId: this.pageHaspId,
                pageObj: this.pageObj,
                pageLines: this.pageLines,
            },
        };
    }
}
